import { CheckOutlined } from '@ant-design/icons';
import { Modal } from 'antd';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { askAssistant } from 'redux/slice/chatBubbleSlice';
import { AppDispatch } from 'redux/store';
import useAiAssistantAccess from 'utils/useAiAssistantAccess';
import { TUTORIAL_COMPLETION, TUTORIALS } from './steps';
import { useTutorial } from './TutorialContext';
import { TutorialStep } from './types';
import styles from './complete.module.scss';
import './complete.scss';

/** Sparkle glyph for the assistant hand-off, matching the step panel's. */
const SparkleIcon = () => (
	<svg
		width="13"
		height="13"
		viewBox="0 0 24 24"
		fill="currentColor"
		aria-hidden="true"
	>
		<path d="M12 2.5l1.9 5.1 5.1 1.9-5.1 1.9L12 16.5l-1.9-5.1L5 9.5l5.1-1.9L12 2.5Z" />
		<path d="M18.5 15l.9 2.3 2.3.9-2.3.9-.9 2.4-.9-2.4-2.3-.9 2.3-.9.9-2.3Z" />
	</svg>
);

/** One recap row per page the tutorial covered, in the order it visited them. */
const summariseStages = (steps: TutorialStep[]) => {
	const stages: { label: string; icon: TutorialStep['icon']; count: number }[] =
		[];
	steps.forEach((step) => {
		const last = stages[stages.length - 1];
		if (last && last.label === step.pageLabel) {
			last.count += 1;
		} else {
			stages.push({ label: step.pageLabel, icon: step.icon, count: 1 });
		}
	});
	return stages;
};

/**
 * Shown once a tutorial is played to the last step — an acknowledgement plus a
 * recap of the pages it covered, so the walkthrough ends on something rather
 * than the panel simply vanishing.
 */
const TutorialComplete = () => {
	const { completedTutorial, dismissCompletion, startTutorial } = useTutorial();
	const dispatch = useDispatch<AppDispatch>();
	const navigate = useNavigate();
	const hasAiAssistantAccess = useAiAssistantAccess();

	if (!completedTutorial) {
		return null;
	}

	const { title, description } = TUTORIAL_COMPLETION[completedTutorial];
	const steps = TUTORIALS[completedTutorial];
	const stages = summariseStages(steps);

	const askAi = () => {
		dispatch(
			askAssistant(
				'I just finished the payroll allocation walkthrough in CostAllocation Pro. What should I check before I publish my first journal entry?'
			)
		);
		dismissCompletion();
		// The assistant now lives only on the /chat page (the floating bubble was
		// removed), so send the finished walkthrough's question there.
		navigate('/chat');
	};

	return (
		<Modal
			open
			onCancel={dismissCompletion}
			rootClassName="cap-tutorial-complete-modal"
			closable={false}
			centered
			width={440}
			footer={null}
		>
			<div className={styles['tutorial-complete']}>
				<span className={styles['tutorial-complete__badge']} aria-hidden="true">
					<CheckOutlined />
				</span>
				<h2 className={styles['tutorial-complete__title']}>{title}</h2>
				<p className={styles['tutorial-complete__description']}>
					{description}
				</p>

				<ul className={styles['tutorial-complete__stages']}>
					{stages.map((stage, index) => (
						<li
							key={stage.label}
							className={styles['tutorial-complete__stage']}
							// Staggered so the recap settles in rather than snapping.
							style={{ animationDelay: `${120 + index * 70}ms` }}
						>
							<span className={styles['tutorial-complete__stage-icon']}>
								{stage.icon}
							</span>
							<span className={styles['tutorial-complete__stage-label']}>
								{stage.label}
							</span>
							<span className={styles['tutorial-complete__stage-count']}>
								{stage.count} {stage.count === 1 ? 'step' : 'steps'}
							</span>
							<CheckOutlined
								className={styles['tutorial-complete__stage-check']}
							/>
						</li>
					))}
				</ul>

				{hasAiAssistantAccess && (
					<button
						type="button"
						className={styles['tutorial-complete__ask-ai']}
						onClick={askAi}
					>
						<SparkleIcon />
						Ask AI what to check first
					</button>
				)}

				<div className={styles['tutorial-complete__actions']}>
					<button
						type="button"
						className={styles['tutorial-complete__replay']}
						onClick={() => startTutorial(completedTutorial)}
					>
						Replay tutorial
					</button>
					<button
						type="button"
						className={styles['tutorial-complete__done']}
						onClick={dismissCompletion}
					>
						Done
					</button>
				</div>
			</div>
		</Modal>
	);
};

export default TutorialComplete;
